import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Chat, IOStatusEnum } from '@matap/shared';
import styled from 'styled-components/native';
import Slider from '@react-native-community/slider';
import { Platform, Animated, TouchableOpacity } from 'react-native';
import { useFileAsset } from '@/hooks/use.file.asset';
import { Box } from '@/components/base/box';
import { createAudioPlayer, AudioPlayer } from 'expo-audio';
import { AppIconButton } from '@/components/base/app.icon.button';
import { AppCardView } from '@/components/base/app.card.view';
import { ChatFooterView } from './chat.footer.view';
import { DownloadUploadStateView } from './download.upload.state.view';
import { ChatContainer } from './chat.container';
import { AppTextView } from '@/components/base/app.text.view';
import { playbackStarted, registerHandle, unregisterHandle } from '../../../utils/playback.manager';

interface Props {
  chat: Chat;
  isSentByUser: boolean;
  temporary: boolean;
  onLongPress: (e: any) => void;
}

function BaseChatVoiceView({ chat, isSentByUser, temporary, onLongPress }: Props) {
  const { state, mediaMetaData } = useFileAsset(chat, temporary);
  const playerRef = useRef<AudioPlayer | null>(null);

  // Triggers a re-render so VoiceSlider receives the populated playerRef
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);

  const listener = useCallback((status: any) => {
    setIsPlaying(status.timeControlStatus === 'playing');
    if (status.duration) {
      setDuration(status.duration);
    }
    if (status.didJustFinish) {
      playerRef.current?.pause();
      playerRef.current?.seekTo(0);
    }
  }, []);

  useEffect(() => {
    if (isPlaying) {
      playbackStarted(playerRef.current);
    }
  }, [isPlaying]);

  useEffect(() => {
    return () => {
      if (playerRef.current) {
        playerRef.current.removeListener('playbackStatusUpdate', listener);
        unregisterHandle(playerRef.current);
        playerRef.current.release();
      }
    };
  }, [listener]);

  const toggle = useCallback(() => {
    if (playerRef.current) {
      if (isPlaying) {
        playerRef.current.pause();
      } else {
        playerRef.current.play();
      }
    } else if (state?.localAddress) {
      // Create the instance just-in-time
      const player = createAudioPlayer(state?.localAddress, { updateInterval: 100 });
      player.addListener('playbackStatusUpdate', listener);
      playerRef.current = player;

      // Trigger a re-render so VoiceSlider gets the newly populated playerRef.current
      setIsPlayerReady(true);
      registerHandle(playerRef.current);
      player.play();
    }
  }, [isPlaying, listener, state?.localAddress]);

  const seek = useCallback((value: number) => {
    playerRef.current?.seekTo(value / 1000).then(() => {
      playerRef.current?.play();
    });
  }, []);

  const pauseForSeek = useCallback(() => {
    if (isPlaying) playerRef.current?.pause();
  }, [isPlaying]);

  const totalDurationMs = duration ? duration * 1000 : mediaMetaData?.duration || 0;

  return (
    <OuterContainer isSentByUser={isSentByUser} onLongPress={onLongPress}>
      <VoiceCard alignItems={'center'} justifyContent={'center'} flexDirection={'column'} shadowOffsetX={0} shadowOffsetY={2} blurRadius={6}>
        <Box flexDirection={'row'} alignItems={'center'}>
          <IconContainer shadowOffsetX={0} shadowOffsetY={0} blurRadius={4}>
            {state && state.state === IOStatusEnum.READY && (
              <SoftIconButton onPress={toggle} onLongPress={onLongPress} icon={isPlaying ? 'pause_play' : 'play'} color={'#999999'} size={'big'} />
            )}
            {state && state.state !== IOStatusEnum.READY && <IOStatusView fileState={state} chatId={chat._id} />}
          </IconContainer>

          <VoiceSlider onSlidingStart={pauseForSeek} onSlidingComplete={seek} player={isPlayerReady ? playerRef.current : null} totalDurationMs={totalDurationMs} />
        </Box>

        <FooterContainer>
          <ChatFooterView chat={chat} isSentByUser={isSentByUser} />
        </FooterContainer>
      </VoiceCard>
    </OuterContainer>
  );
}

function formatTime(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
}

function VoiceSlider({
  player,
  totalDurationMs,
  onSlidingStart,
  onSlidingComplete,
}: {
  player: AudioPlayer | null;
  totalDurationMs: number;
  onSlidingStart: () => void;
  onSlidingComplete: (val: number) => void;
}) {
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    if (!player) {
      setCurrentTime(0);
      return;
    }
    const listener = player.addListener('playbackStatusUpdate', (status: any) => {
      if (!status.didJustFinish) {
        setCurrentTime(status.currentTime || 0);
      } else {
        setCurrentTime(0);
      }
    });
    return () => listener.remove();
  }, [player]);

  return (
    <SliderWrapper>
      <SliderView
        minimumValue={0}
        maximumValue={totalDurationMs > 0 ? totalDurationMs : 1}
        value={currentTime ? currentTime * 1000 : 0}
        onSlidingStart={onSlidingStart}
        onSlidingComplete={onSlidingComplete}
        minimumTrackTintColor={'#d0d0d0'}
        thumbTintColor={'#f0f0f0'}
      />
      <TimeRow>
        {currentTime ? (
          <>
            <TimeText>{formatTime(currentTime ? currentTime * 1000 : 0)}</TimeText>
            <Slash text={'/'} />
          </>
        ) : undefined}
        <TimeText>{formatTime(totalDurationMs)}</TimeText>
      </TimeRow>
    </SliderWrapper>
  );
}

// Soft pressable icon wrapper to give a gentle scale feedback
function SoftIconButton({ onPress, onLongPress, icon, size, color }: { onPress?: () => void; onLongPress?: (e: any) => void; icon: any; size?: any; color?: any }) {
  const scale = useRef(new Animated.Value(1)).current;
  const onPressIn = () => Animated.timing(scale, { toValue: 0.8, duration: 50, useNativeDriver: true }).start();
  const onPressOut = () => Animated.spring(scale, { toValue: 1, useNativeDriver: true, friction: 8, tension: 80 }).start();

  return (
    <SoftButton onPress={onPress} onLongPress={onLongPress} onPressIn={onPressIn} onPressOut={onPressOut} activeOpacity={1}>
      <Animated.View style={{ transform: [{ scale }], alignItems: 'center', justifyContent: 'center' }}>
        <AppIconButton icon={icon} size={size} color={color} />
      </Animated.View>
    </SoftButton>
  );
}

const OuterContainer = styled(ChatContainer)<{ isSentByUser: boolean }>`
  padding: 5px;
  border-top-left-radius: 28px;
  border-top-right-radius: 28px;
  border-bottom-left-radius: ${({ isSentByUser }) => (isSentByUser ? 28 : 0)}px;
  border-bottom-right-radius: ${({ isSentByUser }) => (isSentByUser ? 0 : 28)}px;
`;

const FooterContainer = styled(Box)`
  position: absolute;
  bottom: 2px;
  right: 17px;
`;

const SliderWrapper = styled(Box)`
  flex-direction: column;
  align-items: flex-start;
`;

const TimeRow = styled(Box)`
  flex-direction: row;
  justify-content: space-between;
  max-width: 180px;
  margin-left: 10px;
  margin-bottom: -13px;
  gap: 2px;
  position: absolute;
  bottom: 0%;
`;

const TimeText = styled(AppTextView)`
  font-size: 10px;
  color: #aaaaaa;
  min-width: 22px;
`;

const Slash = styled(AppTextView)`
  font-size: 10px;
  color: #aaaaaa;
`;

const SliderView = styled(Slider)`
  width: ${({ theme }) => theme.wp(40)}px;
  max-width: 180px;
  padding: 0;
  ${Platform.OS === 'web' && `margin-horizontal:10px`}
`;

const IconContainer = styled(AppCardView)`
  width: 56px;
  height: 56px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background-color: #fafafa;
`;

const IOStatusView = styled(DownloadUploadStateView)`
  width: 46px;
  height: 46px;
`;

const VoiceCard = styled(AppCardView)`
  min-height: 60px;
  border-radius: 28px;
  background-color: #ffffff;
  padding: 5px;
`;

const SoftButton = styled(TouchableOpacity)`
  padding: 6px;
`;

export const ChatVoiceView = React.memo(BaseChatVoiceView);
